import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = {
  class: "info-hero",
  "aria-labelledby": "info-center-title"
}
const _hoisted_2 = {
  class: "info-hero__photo",
  "aria-hidden": "true"
}
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "info-hero__inner" }
const _hoisted_5 = { class: "info-hero__kicker" }
const _hoisted_6 = { id: "info-center-title" }
const _hoisted_7 = {
  key: 0,
  class: "info-hero__lead"
}
const _hoisted_8 = {
  key: 0,
  class: "info-center__adminbar"
}
const _hoisted_9 = {
  key: 0,
  class: "info-center__note",
  role: "status"
}
const _hoisted_10 = { class: "info-center__stage-photo" }
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "info-center__file" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "info-center__edit-actions" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "info-center__body" }
const _hoisted_18 = { class: "info-center__side" }
const _hoisted_19 = {
  class: "info-center__side-nav",
  "aria-label": "정보마당 폴더"
}
const _hoisted_20 = { class: "info-center__side-group" }
const _hoisted_21 = ["aria-current"]
const _hoisted_22 = ["aria-expanded", "aria-label", "onClick"]
const _hoisted_23 = ["aria-current", "onClick"]
const _hoisted_24 = {
  key: 0,
  class: "info-card__tag is-hidden"
}
const _hoisted_25 = { class: "info-side__help" }
const _hoisted_26 = {
  class: "info-side__help-icon",
  "aria-hidden": "true"
}
const _hoisted_27 = {
  key: 0,
  class: "info-center__status"
}
const _hoisted_28 = {
  key: 1,
  class: "info-center__status"
}
const _hoisted_29 = {
  key: 2,
  class: "info-center__reader"
}
const _hoisted_30 = ["srcdoc", "title"]
const _hoisted_31 = {
  key: 1,
  class: "info-center__list-head"
}
const _hoisted_32 = { class: "info-center__tools" }
const _hoisted_33 = { class: "info-center__sort" }
const _hoisted_34 = {
  key: 2,
  class: "info-center__status"
}
const _hoisted_35 = {
  key: 3,
  class: "info-center__status"
}
const _hoisted_36 = {
  key: 4,
  class: "info-center__status"
}
const _hoisted_37 = {
  key: 5,
  class: "info-center__cards"
}
const _hoisted_38 = ["src"]
const _hoisted_39 = { class: "info-card__body" }
const _hoisted_40 = { class: "info-card__date" }
const _hoisted_41 = ["datetime"]
const _hoisted_42 = { class: "info-card__title" }
const _hoisted_43 = {
  key: 0,
  class: "info-card__summary"
}
const _hoisted_44 = { class: "info-card__tags" }
const _hoisted_45 = {
  key: 0,
  class: "info-card__tag is-hidden"
}
const _hoisted_46 = {
  key: 6,
  class: "info-center__pages",
  "aria-label": "글 페이지"
}
const _hoisted_47 = ["disabled"]
const _hoisted_48 = {
  key: 0,
  class: "info-center__pages-dots"
}
const _hoisted_49 = ["aria-current", "onClick"]
const _hoisted_50 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'
import {
  fetchAdminInfo,
  fetchPublicInfo,
  fetchPublicInfoDoc,
  type InfoDoc,
  type InfoFolder,
} from '@/api/infoPlaza'
import {
  restoreInfoStage,
  saveInfoStage,
  uploadInfoImage,
  type InfoStage,
} from '@/api/infoCenter'

const PAGE_SIZE = 6
const heroFallback = '/images/info/hero-desk.jpg'

export default /*@__PURE__*/_defineComponent({
  __name: 'InfoCenterPage',
  setup(__props) {

const thumbPool = [
  '/images/info/thumb-a.jpg',
  '/images/info/thumb-b.jpg',
  '/images/info/thumb-c.jpg',
  '/images/info/thumb-d.jpg',
]
const tagPalette = ['#d4533c', '#3b6fd8', '#1f8a4c', '#6a568f', '#243044', '#c47a2c']

const { isAdminLoggedIn, initAdminAuth } = useAdminAuth()
const route = useRoute()
const router = useRouter()

const folders = ref<InfoFolder[]>([])
const docs = ref<InfoDoc[]>([])
const openIds = ref<Set<number>>(new Set())
const treeReady = ref(false)
const stage = ref<InfoStage>({ kicker: 'INFORMATION CENTER', title: '정보마당', desc: '', heroImage: '' })
const stageDraft = reactive({ kicker: '', title: '', desc: '', heroImage: '' })
const stageSaved = ref('')
const folderId = ref(0)
const sort = ref('latest')
const loading = ref(true)
const errorMessage = ref('')
const note = ref('')
const saving = ref(false)
const editing = ref(false)
const page = ref(1)
const mainEl = ref<HTMLElement | null>(null)
const frameEl = ref<HTMLIFrameElement | null>(null)
const activeDoc = ref<InfoDoc | null>(null)
const docLoading = ref(false)
const docError = ref('')

const viewingDoc = computed(() => route.name === 'info-detail')
const htmlBody = computed(() => {
  const content = activeDoc.value?.content || ''
  if (/<\s*(!doctype|html)\b/i.test(content)) return content
  return `<!DOCTYPE html><html lang="ko"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>:root{color-scheme:light}body{margin:0;padding:4px 0;font-family:'Noto Sans KR','Apple SD Gothic Neo',system-ui,sans-serif;line-height:1.85;color:#1b1f27;word-break:keep-all}img{max-width:100%;height:auto}table{border-collapse:collapse}</style></head><body>${content}</body></html>`
})

const heroSrc = computed(() => stage.value.heroImage || heroFallback)

const childrenOf = computed(() => {
  const map = new Map<number, InfoFolder[]>()
  folders.value.forEach((folder) => {
    const key = folder.parentIdx ?? 0
    const list = map.get(key) || []
    list.push(folder)
    map.set(key, list)
  })
  map.forEach((list) => list.sort((a, b) => a.sort - b.sort || a.idx - b.idx))
  return map
})

const visibleFolders = computed(() => {
  const rows: Array<InfoFolder & { depth: number; hasChildren: boolean }> = []
  const walk = (parentIdx: number, depth: number) => {
    ;(childrenOf.value.get(parentIdx) || []).forEach((folder) => {
      const hasChildren = (childrenOf.value.get(folder.idx) || []).length > 0
      rows.push({ ...folder, depth, hasChildren })
      if (hasChildren && openIds.value.has(folder.idx)) walk(folder.idx, depth + 1)
    })
  }
  walk(0, 0)
  return rows
})

const filteredDocs = computed(() => {
  const allowed = folderId.value ? new Set(folderScope(folderId.value)) : null
  const rows = docs.value.filter((doc) => !allowed || allowed.has(doc.folderIdx))
  rows.sort((a, b) => {
    if (sort.value === 'title') return a.title.localeCompare(b.title, 'ko') || b.idx - a.idx
    if (sort.value === 'oldest') return a.createdAt.localeCompare(b.createdAt) || a.idx - b.idx
    return b.createdAt.localeCompare(a.createdAt) || b.idx - a.idx
  })
  return rows
})

const heading = computed(() => {
  if (!folderId.value) return '전체'
  return folders.value.find((folder) => folder.idx === folderId.value)?.name || '전체'
})

const pageCount = computed(() => Math.max(1, Math.ceil(filteredDocs.value.length / PAGE_SIZE)))
const pageDocs = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filteredDocs.value.slice(start, start + PAGE_SIZE)
})
const pageItems = computed(() => {
  const total = pageCount.value
  const current = page.value
  const items: Array<{ key: string; n?: number; dots?: boolean }> = []
  const push = (n: number) => items.push({ key: `p${n}`, n })
  if (total <= 7) {
    for (let i = 1; i <= total; i += 1) push(i)
    return items
  }
  const windowStart = Math.max(2, Math.min(current - 1, total - 4))
  const windowEnd = Math.min(total - 1, windowStart + 3)
  push(1)
  if (windowStart > 2) items.push({ key: 'd1', dots: true })
  for (let i = windowStart; i <= windowEnd; i += 1) push(i)
  if (windowEnd < total - 1) items.push({ key: 'd2', dots: true })
  push(total)
  return items
})

function folderScope(idx: number) {
  const ids = [idx]
  const walk = (parent: number) => {
    ;(childrenOf.value.get(parent) || []).forEach((folder) => {
      ids.push(folder.idx)
      walk(folder.idx)
    })
  }
  walk(idx)
  return ids
}

function countOf(idx: number) {
  const ids = new Set(folderScope(idx))
  return docs.value.filter((doc) => ids.has(doc.folderIdx)).length
}

function isOpen(idx: number) {
  return openIds.value.has(idx)
}

function toggleFolder(idx: number) {
  const next = new Set(openIds.value)
  if (next.has(idx)) next.delete(idx)
  else next.add(idx)
  openIds.value = next
}

function formatDate(value: string) {
  return String(value || '').slice(0, 10).replace(/-/g, '.')
}

function cardImage(item: InfoDoc) {
  if (item.cover) return item.cover
  return thumbPool[Math.abs(item.idx || 0) % thumbPool.length]
}

function tagColor(folderIdx: number) {
  return tagPalette[Math.abs(folderIdx || 0) % tagPalette.length]
}

function rememberStage() {
  stageSaved.value = JSON.stringify({ ...stageDraft })
}

function applyStage(next?: InfoStage) {
  if (!next) return
  stage.value = next
  Object.assign(stageDraft, {
    kicker: next.kicker,
    title: next.title,
    desc: next.desc,
    heroImage: next.heroImage,
  })
  rememberStage()
}

function applyRouteQuery() {
  const raw = Number(route.query.folder)
  folderId.value = Number.isFinite(raw) && raw > 0 ? raw : 0
  const nextSort = String(route.query.sort || 'latest')
  sort.value = nextSort === 'oldest' || nextSort === 'title' ? nextSort : 'latest'
}

function buildQuery() {
  const query: Record<string, string> = {}
  if (folderId.value) query.folder = String(folderId.value)
  if (sort.value !== 'latest') query.sort = sort.value
  return query
}

function sameQuery(next: Record<string, string>) {
  const current = {
    folder: String(route.query.folder || ''),
    sort: String(route.query.sort || ''),
  }
  return current.folder === (next.folder || '') && current.sort === (next.sort || '')
}

function commitFilters() {
  page.value = 1
  const next = buildQuery()
  if (sameQuery(next)) {
    applyRouteQuery()
    return
  }
  void router.replace({ query: next })
}

function selectFolder(idx: number) {
  folderId.value = idx
  page.value = 1
  if (route.name === 'info-detail') {
    void router.push({ name: 'info-center', query: buildQuery() })
    return
  }
  commitFilters()
}

function revealFolder(idx: number) {
  const next = new Set(openIds.value)
  let current = folders.value.find((folder) => folder.idx === idx)
  while (current?.parentIdx) {
    next.add(current.parentIdx)
    const parentIdx = current.parentIdx
    current = folders.value.find((folder) => folder.idx === parentIdx)
  }
  openIds.value = next
}

function fitFrame() {
  const frame = frameEl.value
  const doc = frame?.contentDocument
  if (!frame || !doc) return
  frame.style.height = '0px'
  const contentHeight = Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0)
  const minHeight = Math.max(640, window.innerHeight - 160)
  frame.style.height = `${Math.max(contentHeight, minHeight)}px`
}

function onFrameLoad() {
  fitFrame()
  frameEl.value?.contentDocument?.querySelectorAll('img').forEach((img) => {
    if (img.complete) return
    img.addEventListener('load', fitFrame, { once: true })
  })
}

async function focusReader() {
  await nextTick()
  mainEl.value?.scrollIntoView({ block: 'start', behavior: 'auto' })
}

async function loadDoc() {
  const code = String(route.params.code || '')
  if (route.name !== 'info-detail' || !code) return
  docLoading.value = true
  docError.value = ''
  activeDoc.value = null
  try {
    const doc = await fetchPublicInfoDoc(code)
    if (route.name !== 'info-detail' || String(route.params.code || '') !== code) return
    activeDoc.value = doc
    if (doc.folderIdx) {
      folderId.value = doc.folderIdx
      revealFolder(doc.folderIdx)
    }
    void focusReader()
  } catch (error) {
    if (route.name !== 'info-detail' || String(route.params.code || '') !== code) return
    if (!isAdminLoggedIn.value) {
      docError.value = error instanceof Error ? error.message : '콘텐츠를 찾을 수 없습니다.'
    } else {
      try {
        const data = await fetchAdminInfo()
        const doc = data.docs.find((item) => item.id === code) ?? null
        activeDoc.value = doc
        if (doc?.folderIdx) {
          folderId.value = doc.folderIdx
          revealFolder(doc.folderIdx)
        }
        if (!doc) docError.value = '콘텐츠를 찾을 수 없습니다.'
        else void focusReader()
      } catch (adminError) {
        docError.value = adminError instanceof Error ? adminError.message : '콘텐츠를 찾을 수 없습니다.'
      }
    }
  } finally {
    if (String(route.params.code || '') === code) docLoading.value = false
  }
}

function goPage(n: number) {
  page.value = Math.min(pageCount.value, Math.max(1, n))
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const data = isAdminLoggedIn.value ? await fetchAdminInfo() : await fetchPublicInfo()
    folders.value = data.folders
    docs.value = data.docs
    applyStage(data.stage)
    if (activeDoc.value?.folderIdx) revealFolder(activeDoc.value.folderIdx)
    if (!treeReady.value) {
      openIds.value = new Set(
        folders.value
          .filter((folder) => folders.value.some((child) => child.parentIdx === folder.idx))
          .map((folder) => folder.idx),
      )
      treeReady.value = true
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정보마당을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function toggleEdit() {
  if (!isAdminLoggedIn.value) return
  editing.value = !editing.value
  if (editing.value) rememberStage()
}

function revertStage() {
  if (!stageSaved.value) return
  Object.assign(stageDraft, JSON.parse(stageSaved.value))
}

async function readImage(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return ''
  return uploadInfoImage(file)
}

async function onStageImage(event: Event) {
  try {
    const url = await readImage(event)
    if (url) stageDraft.heroImage = url
  } catch (error) {
    note.value = error instanceof Error ? error.message : '이미지를 올리지 못했습니다.'
  }
}

async function saveStage() {
  saving.value = true
  note.value = ''
  try {
    applyStage(await saveInfoStage({ ...stageDraft }))
    note.value = '소개 영역을 저장했습니다.'
  } catch (error) {
    note.value = error instanceof Error ? error.message : '소개 영역을 저장하지 못했습니다.'
  } finally {
    saving.value = false
  }
}

async function onRestoreStage() {
  saving.value = true
  note.value = ''
  try {
    applyStage(await restoreInfoStage())
    note.value = '직전 소개 영역으로 되돌렸습니다.'
  } catch (error) {
    note.value = error instanceof Error ? error.message : '되돌리지 못했습니다.'
  } finally {
    saving.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    if (route.name === 'info-detail') {
      void loadDoc()
      return
    }
    if (route.name !== 'info-center') return
    activeDoc.value = null
    docError.value = ''
    applyRouteQuery()
    page.value = 1
  },
)

watch(isAdminLoggedIn, (on) => {
  if (!on) editing.value = false
  if (!loading.value) void load()
})

watch(pageCount, (total) => {
  if (page.value > total) page.value = total
})

onMounted(async () => {
  window.addEventListener('resize', fitFrame)
  applyRouteQuery()
  await initAdminAuth()
  await Promise.all([load(), loadDoc()])
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', fitFrame)
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["info-center", { 'is-editing': editing.value, 'is-reading': viewingDoc.value }])
  }, [
    _createElementVNode("section", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("img", {
          src: heroSrc.value,
          alt: ""
        }, null, 8, _hoisted_3)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("p", _hoisted_5, _toDisplayString(stage.value.kicker), 1),
        _createElementVNode("h1", _hoisted_6, _toDisplayString(stage.value.title), 1),
        (stage.value.desc)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(stage.value.desc), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    (_unref(isAdminLoggedIn))
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createElementVNode("button", {
            type: "button",
            class: "portal-btn portal-btn--secondary",
            onClick: toggleEdit
          }, _toDisplayString(editing.value ? '편집 끝내기' : '소개 편집'), 1),
          _createVNode(_component_router_link, {
            to: "/info/library",
            class: "info-center__library"
          }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode("폴더·HTML 관리", -1)
            ]))]),
            _: 1
          }),
          (note.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(note.value), 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (_unref(isAdminLoggedIn) && editing.value)
      ? (_openBlock(), _createElementBlock("form", {
          key: 1,
          class: "info-center__stage-edit",
          onSubmit: _withModifiers(saveStage, ["prevent"])
        }, [
          _cache[13] || (_cache[13] = _createElementVNode("p", { class: "info-center__edit-label" }, "소개 영역", -1)),
          _createElementVNode("label", null, [
            _cache[9] || (_cache[9] = _createTextVNode("작은 문구 ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((stageDraft.kicker) = $event)),
              class: "portal-input",
              maxlength: "40"
            }, null, 512), [
              [_vModelText, stageDraft.kicker]
            ])
          ]),
          _createElementVNode("label", null, [
            _cache[10] || (_cache[10] = _createTextVNode("제목 ", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((stageDraft.title) = $event)),
              class: "portal-input",
              maxlength: "80"
            }, null, 512), [
              [_vModelText, stageDraft.title]
            ])
          ]),
          _createElementVNode("label", null, [
            _cache[11] || (_cache[11] = _createTextVNode("설명 ", -1)),
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((stageDraft.desc) = $event)),
              class: "portal-input",
              rows: "2",
              maxlength: "300"
            }, null, 512), [
              [_vModelText, stageDraft.desc]
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("img", {
              src: stageDraft.heroImage || heroFallback,
              alt: ""
            }, null, 8, _hoisted_11),
            _createElementVNode("label", _hoisted_12, [
              _cache[12] || (_cache[12] = _createTextVNode(" 배경 이미지 ", -1)),
              _createElementVNode("input", {
                type: "file",
                accept: "image/jpeg,image/png,image/webp,image/gif",
                onChange: onStageImage
              }, null, 32)
            ]),
            _createElementVNode("button", {
              type: "button",
              class: "portal-btn portal-btn--secondary",
              disabled: !stageDraft.heroImage,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (stageDraft.heroImage = ''))
            }, " 이미지 제거 ", 8, _hoisted_13)
          ]),
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("button", {
              type: "button",
              class: "portal-btn portal-btn--secondary",
              onClick: revertStage
            }, "저장 전으로"),
            _createElementVNode("button", {
              type: "button",
              class: "portal-btn portal-btn--secondary",
              disabled: saving.value || !stage.value.hasPrev,
              onClick: onRestoreStage
            }, " 직전 저장본 ", 8, _hoisted_15),
            _createElementVNode("button", {
              type: "submit",
              class: "portal-btn portal-btn--primary",
              disabled: saving.value
            }, "소개 저장", 8, _hoisted_16)
          ])
        ], 32))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("aside", _hoisted_18, [
        _cache[18] || (_cache[18] = _createElementVNode("p", { class: "info-center__side-title" }, "폴더", -1)),
        _createElementVNode("nav", _hoisted_19, [
          _createElementVNode("div", _hoisted_20, [
            _createElementVNode("div", {
              class: _normalizeClass(["info-center__side-row", { 'is-on': folderId.value === 0 }])
            }, [
              _createElementVNode("button", {
                type: "button",
                class: "info-center__side-item",
                "aria-current": folderId.value === 0 ? 'true' : undefined,
                onClick: _cache[4] || (_cache[4] = ($event: any) => (selectFolder(0)))
              }, [
                _createVNode(AppIcon, {
                  icon: "mdi-home-outline",
                  size: 18
                }),
                _cache[14] || (_cache[14] = _createElementVNode("span", null, "전체", -1)),
                _createElementVNode("em", null, _toDisplayString(docs.value.length), 1)
              ], 8, _hoisted_21)
            ], 2)
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFolders.value, (row) => {
            return (_openBlock(), _createElementBlock("div", {
              key: row.idx,
              class: "info-center__side-group"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["info-center__side-row", { 'is-on': folderId.value === row.idx }]),
                style: _normalizeStyle({ paddingLeft: `${row.depth * 14}px` })
              }, [
                (row.hasChildren)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "info-center__chev",
                      "aria-expanded": isOpen(row.idx),
                      "aria-label": `${row.name} ${isOpen(row.idx) ? '접기' : '펼치기'}`,
                      onClick: ($event: any) => (toggleFolder(row.idx))
                    }, [
                      _createVNode(AppIcon, {
                        icon: isOpen(row.idx) ? 'mdi-chevron-down' : 'mdi-chevron-right',
                        size: 16
                      }, null, 8, ["icon"])
                    ], 8, _hoisted_22))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  class: "info-center__side-item",
                  "aria-current": folderId.value === row.idx ? 'true' : undefined,
                  onClick: ($event: any) => (selectFolder(row.idx))
                }, [
                  _createVNode(AppIcon, {
                    icon: "mdi-folder-outline",
                    size: 18
                  }),
                  _createElementVNode("span", null, _toDisplayString(row.name), 1),
                  (row.status === 'HIDDEN')
                    ? (_openBlock(), _createElementBlock("i", _hoisted_24, "숨김"))
                    : _createCommentVNode("", true),
                  _createElementVNode("em", null, _toDisplayString(countOf(row.idx)), 1)
                ], 8, _hoisted_23)
              ], 6)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_25, [
          _createElementVNode("span", _hoisted_26, [
            _createVNode(AppIcon, {
              icon: "mdi-headset",
              size: 22
            })
          ]),
          _cache[16] || (_cache[16] = _createElementVNode("p", { class: "info-side__help-title" }, "궁금한 사항이 있으신가요?", -1)),
          _cache[17] || (_cache[17] = _createElementVNode("p", { class: "info-side__help-copy" }, [
            _createTextVNode("전문 상담사가 친절하게"),
            _createElementVNode("br"),
            _createTextVNode("안내해드립니다.")
          ], -1)),
          _createVNode(_component_router_link, {
            to: "/consult-board/write",
            class: "info-side__help-btn"
          }, {
            default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
              _createTextVNode("상담하기 ", -1),
              _createElementVNode("span", { "aria-hidden": "true" }, "→", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      _createElementVNode("div", {
        ref_key: "mainEl",
        ref: mainEl,
        class: "info-center__main"
      }, [
        (viewingDoc.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (docLoading.value && !activeDoc.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_27, "불러오는 중…"))
                : (docError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(docError.value), 1))
                  : (activeDoc.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                        _createElementVNode("iframe", {
                          ref_key: "frameEl",
                          ref: frameEl,
                          class: "info-center__html",
                          srcdoc: htmlBody.value,
                          sandbox: "allow-same-origin",
                          title: activeDoc.value.title,
                          referrerpolicy: "no-referrer",
                          onLoad: onFrameLoad
                        }, null, 40, _hoisted_30)
                      ]))
                    : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true),
        (!viewingDoc.value)
          ? (_openBlock(), _createElementBlock("header", _hoisted_31, [
              _createElementVNode("div", null, [
                _createElementVNode("h2", null, _toDisplayString(heading.value), 1),
                _createElementVNode("p", null, "총 " + _toDisplayString(filteredDocs.value.length) + "개의 게시물이 있습니다.", 1)
              ]),
              _createElementVNode("div", _hoisted_32, [
                _createElementVNode("label", _hoisted_33, [
                  _cache[20] || (_cache[20] = _createElementVNode("span", { class: "info-center__sr" }, "정렬", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((sort).value = $event)),
                    onChange: commitFilters
                  }, [...(_cache[19] || (_cache[19] = [
                    _createElementVNode("option", { value: "latest" }, "최신순", -1),
                    _createElementVNode("option", { value: "oldest" }, "오래된순", -1),
                    _createElementVNode("option", { value: "title" }, "제목순", -1)
                  ]))], 544), [
                    [_vModelSelect, sort.value]
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        (!viewingDoc.value && loading.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_34, "불러오는 중…"))
          : (!viewingDoc.value && errorMessage.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_35, _toDisplayString(errorMessage.value), 1))
            : (!viewingDoc.value && !filteredDocs.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_36, "등록된 글이 없습니다."))
              : (!viewingDoc.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageDocs.value, (item) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: item.idx,
                        class: "info-card"
                      }, [
                        _createVNode(_component_router_link, {
                          to: `/info/${item.id}`,
                          class: "info-card__photo"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("img", {
                              src: cardImage(item),
                              alt: ""
                            }, null, 8, _hoisted_38)
                          ]),
                          _: 2
                        }, 1032, ["to"]),
                        _createElementVNode("div", _hoisted_39, [
                          _createElementVNode("p", _hoisted_40, [
                            _createVNode(AppIcon, {
                              icon: "mdi-calendar-blank-outline",
                              size: 15
                            }),
                            _createElementVNode("time", {
                              datetime: item.createdAt
                            }, _toDisplayString(formatDate(item.createdAt)), 9, _hoisted_41)
                          ]),
                          _createElementVNode("h3", _hoisted_42, [
                            _createVNode(_component_router_link, {
                              to: `/info/${item.id}`
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(item.title), 1)
                              ]),
                              _: 2
                            }, 1032, ["to"])
                          ]),
                          (item.summary)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_43, _toDisplayString(item.summary), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_44, [
                            _createElementVNode("span", {
                              class: "info-card__tag",
                              style: _normalizeStyle({ background: tagColor(item.folderIdx) })
                            }, _toDisplayString(item.folderName || '정보마당'), 5),
                            (item.status === 'HIDDEN')
                              ? (_openBlock(), _createElementBlock("span", _hoisted_45, "숨김"))
                              : _createCommentVNode("", true)
                          ])
                        ])
                      ]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
        (!viewingDoc.value && pageCount.value > 1)
          ? (_openBlock(), _createElementBlock("nav", _hoisted_46, [
              _createElementVNode("button", {
                type: "button",
                class: "info-center__pages-step",
                disabled: page.value === 1,
                "aria-label": "이전 페이지",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (goPage(page.value - 1)))
              }, "‹", 8, _hoisted_47),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageItems.value, (item) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: item.key
                }, [
                  (item.dots)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_48, "…"))
                    : (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        class: _normalizeClass(["info-center__pages-num", { 'is-on': item.n === page.value }]),
                        "aria-current": item.n === page.value ? 'page' : undefined,
                        onClick: ($event: any) => (goPage(item.n!))
                      }, _toDisplayString(item.n), 11, _hoisted_49))
                ], 64))
              }), 128)),
              _createElementVNode("button", {
                type: "button",
                class: "info-center__pages-step",
                disabled: page.value === pageCount.value,
                "aria-label": "다음 페이지",
                onClick: _cache[7] || (_cache[7] = ($event: any) => (goPage(page.value + 1)))
              }, "›", 8, _hoisted_50)
            ]))
          : _createCommentVNode("", true)
      ], 512)
    ])
  ], 2))
}
}

})